/** 《金脉·苔光矿谷》数值与类型配置 */

export type MineralKind =
  | 'goldNugget'
  | 'goldBlock'
  | 'diamond'
  | 'cyanCrystal'
  | 'magentaCrystal'
  | 'rock'
  | 'dynamite'
  | 'trash'
  | 'relic'
  | 'starDust'
  | 'amber' | 'fluorite' | 'pearl' | 'garnet'
  | 'fossil' | 'jade' | 'moonstone' | 'starGeode'
  | 'copper' | 'sapphire' | 'sunstone' | 'obsidian';

export type ChipId =
  | 'magnet'
  | 'blast'
  | 'twinHook'
  | 'timeDilation'
  | 'gravityFlip'
  | 'goldFever'
  | 'pocketClock' | 'seedPouch' | 'lantern' | 'mossShield' | 'surveyLens' | 'starElixir'
  | 'rescueBell' | 'ropeShears';

export type CreatureKind = 'mole' | 'beetle' | 'slime' | 'bat';
export const CREATURE_DEFS: Record<CreatureKind,{name:string;radius:number;speed:number;delay:number;description:string}> = {
  mole:{name:'叶帽鼹鼠',radius:.60,speed:.38,delay:.6,description:'瞄准矿物 3 秒后暂藏 6 秒；命中或风铃会立即归还。'},
  beetle:{name:'晶甲虫',radius:.56,speed:.55,delay:1,description:'沿矿脉巡逻，撞上会收回空钩并停顿 1 秒。'},
  slime:{name:'蘑菇团子',radius:.64,speed:.35,delay:1.5,description:'缓慢弹跳，黏住空钩 1.5 秒；护盾可穿过。'},
  bat:{name:'月翼蝠',radius:.48,speed:.9,delay:.6,description:'在深层快速往返，抓住会让空钩停顿 0.6 秒。'},
};

export type ComboColor = 'gold' | 'cyan' | 'magenta' | 'none';

export type GameMode = 'solo' | 'coop' | 'versus';
export type GameStateName =
  | 'menu'
  | 'chip-select'
  | 'active-play'
  | 'level-clear'
  | 'game-over'
  | 'paused'
  | 'boss-intro'
  | 'two-player'
  | 'auth'
  | 'combo'
  | 'coop'
  | 'versus';

export interface MineralDef {
  kind: MineralKind;
  label: string;
  value: number;
  weight: number;
  radius: number;
  color: string;
  emissive: string;
  combo: ComboColor;
  description?: string;
  effect?: 'time' | 'combo' | 'shield' | 'starlight';
  effectAmount?: number;
  shape: 'octa' | 'dodeca' | 'ico' | 'box' | 'torus' | 'star';
}

export const MINERAL_DEFS: Record<MineralKind, MineralDef> = {
  goldNugget: {
    kind: 'goldNugget',
    label: '金粒',
    value: 45,
    weight: 1,
    radius: 0.43,
    color: '#F5C542',
    emissive: '#F5C542',
    combo: 'gold',
    shape: 'octa',
  },
  goldBlock: {
    kind: 'goldBlock',
    label: '金块',
    value: 220,
    weight: 6,
    radius: 0.95,
    color: '#FFD86B',
    emissive: '#F0B429',
    combo: 'gold',
    shape: 'box',
  },
  diamond: {
    kind: 'diamond',
    label: '钻石',
    value: 250,
    weight: 1.8,
    radius: 0.3,
    color: '#B8F3FF',
    emissive: '#7AE7FF',
    combo: 'cyan',
    shape: 'ico',
  },
  cyanCrystal: {
    kind: 'cyanCrystal',
    label: '薄荷晶',
    value: 100,
    weight: 1.7,
    radius: 0.43,
    color: '#92bfae',
    emissive: '#18B8E0',
    combo: 'cyan',
    shape: 'octa',
  },
  magentaCrystal: {
    kind: 'magentaCrystal',
    label: '玫瑰晶',
    value: 120,
    weight: 1.9,
    radius: 0.44,
    color: '#d79caa',
    emissive: '#D12BA8',
    combo: 'magenta',
    shape: 'dodeca',
  },
  rock: {
    kind: 'rock',
    label: '岩石',
    value: 8,
    weight: 10,
    radius: 1.05,
    color: '#5B6478',
    emissive: '#1A2030',
    combo: 'none',
    shape: 'ico',
  },
  dynamite: {
    kind: 'dynamite',
    label: '炸药桶',
    value: 5,
    weight: 1.2,
    radius: 0.48,
    color: '#FF5A5F',
    emissive: '#FF2A30',
    combo: 'none',
    shape: 'box',
  },
  trash: {
    kind: 'trash',
    label: '废料',
    value: 3,
    weight: 2.8,
    radius: 0.6,
    color: '#6E7A8A',
    emissive: '#243040',
    combo: 'none',
    shape: 'torus',
  },
  relic: {
    kind: 'relic',
    label: '古代遗物',
    value: 400,
    weight: 3.5,
    radius: 0.45,
    color: '#C9A0FF',
    emissive: '#8B5CF6',
    combo: 'magenta',
    shape: 'star',
  },
  starDust: {
    kind: 'starDust',
    label: '星尘',
    value: 55,
    weight: 0.6,
    radius: 0.28,
    color: '#FFF4C2',
    emissive: '#FFE08A',
    combo: 'gold',
    shape: 'star',
  },
  amber: { kind: 'amber', label: '叶影琥珀', value: 90, weight: 1.4, radius: 0.4, color: '#dfa34e', emissive: '#dfa34e', combo: 'gold', shape: 'ico', description: '封存叶片的琥珀，交付后增加 1 秒；本层矿物加时合计最多 8 秒。', effect: 'time', effectAmount: 1 },
  fluorite: { kind: 'fluorite', label: '薄荷萤石', value: 140, weight: 1.8, radius: 0.42, color: '#91c6ad', emissive: '#91c6ad', combo: 'cyan', shape: 'ico', description: '交付后连击窗口增加 2 秒。', effect: 'combo', effectAmount: 2 },
  pearl: { kind: 'pearl', label: '花露珍珠', value: 150, weight: 0.9, radius: 0.27, color: '#f1dfc4', emissive: '#f1dfc4', combo: 'cyan', shape: 'ico', description: '轻盈的珍珠，收钩迅速。' },
  garnet: { kind: 'garnet', label: '莓红石榴石', value: 230, weight: 2.8, radius: 0.42, color: '#cc807f', emissive: '#cc807f', combo: 'magenta', shape: 'ico', description: '花影中结出的红色晶簇。' },
  fossil: { kind: 'fossil', label: '螺旋化石', value: 280, weight: 7, radius: 0.8, color: '#c7a36d', emissive: '#c7a36d', combo: 'gold', shape: 'ico', description: '有些沉重，适合用羽毛祝福搬运。' },
  jade: { kind: 'jade', label: '古树翠玉', value: 300, weight: 3.5, radius: 0.43, color: '#91a675', emissive: '#91a675', combo: 'cyan', shape: 'ico', description: '交付后提供 8 秒护盾，抵挡炸药扣款与生物阻挡。', effect: 'shield', effectAmount: 8 },
  moonstone: { kind: 'moonstone', label: '月眠石', value: 280, weight: 2, radius: 0.34, color: '#b8afd2', emissive: '#b8afd2', combo: 'magenta', shape: 'ico', description: '交付后增加 2 秒；本层矿物加时合计最多 8 秒。', effect: 'time', effectAmount: 2 },
  starGeode: { kind: 'starGeode', label: '星辉晶洞石', value: 450, weight: 4.5, radius: 0.48, color: '#89aabf', emissive: '#89aabf', combo: 'cyan', shape: 'ico', description: '下一件宝物额外获得 20% 收益。', effect: 'starlight', effectAmount: 0.2 },
  copper: { kind:'copper',label:'暖铜矿',value:85,weight:2.6,radius:.62,color:'#bd7855',emissive:'#bd7855',combo:'gold',shape:'ico',description:'块头明显、收益适中，适合练习选角。' },
  sapphire: { kind:'sapphire',label:'深蓝尖晶',value:300,weight:2.2,radius:.27,color:'#6b9ec6',emissive:'#6b9ec6',combo:'cyan',shape:'ico',description:'体积很小、收益较高，适合精准出钩。' },
  sunstone: { kind:'sunstone',label:'暖日晶',value:240,weight:2.8,radius:.36,color:'#e6b564',emissive:'#e6b564',combo:'gold',shape:'ico',description:'藏在深层的黄金色晶簇，可接续金色连击。' },
  obsidian: { kind:'obsidian',label:'紫纹黑曜石',value:330,weight:8,radius:.78,color:'#85708e',emissive:'#85708e',combo:'magenta',shape:'ico',description:'高价但非常沉重；羽毛祝福和绞盘能节省搬运时间。' },

};

export interface ChipDef {
  id: ChipId;
  name: string;
  desc: string;
  cooldown: number;
  duration: number;
  icon: string;
  stage: number;
}

export const CHIP_DEFS: Record<ChipId, ChipDef> = {
  magnet: {
    id: 'magnet',
    stage: 1,
    name: '拾金磁石',
    desc: '8 秒内钩爪吸附小矿石',
    cooldown: 20,
    duration: 8,
    icon: '⚡',
  },
  blast: {
    id: 'blast',
    stage: 1,
    name: '碎石火花',
    desc: '下一次命中岩石时炸碎并震开周围',
    cooldown: 30,
    duration: 0,
    icon: '💥',
  },
  twinHook: {
    id: 'twinHook',
    stage: 1,
    name: '轻巧绞盘',
    desc: '8 秒内收钩速度提升 55%',
    cooldown: 26,
    duration: 8,
    icon: '⚔',
  },
  timeDilation: {
    id: 'timeDilation',
    stage: 2,
    name: '慢时沙漏',
    desc: '5 秒摆锤和生物减速，绳索速度保持正常',
    cooldown: 28,
    duration: 5,
    icon: '⏱',
  },
  gravityFlip: {
    id: 'gravityFlip',
    stage: 4,
    name: '羽毛祝福',
    desc: '6 秒重物变轻、轻物变重',
    cooldown: 35,
    duration: 6,
    icon: '🔄',
  },
  goldFever: {
    id: 'goldFever',
    stage: 5,
    name: '丰收祝愿',
    desc: '本层一次，连击窗口延长且倍率 +0.35',
    cooldown: 999,
    duration: 0,
    icon: '🔥',
  },
  pocketClock: { id: 'pocketClock', name: '叶片怀表', desc: '每层一次，为全场增加 6 秒', cooldown: 999, duration: 0, icon: '◷', stage: 1 },
  seedPouch: { id: 'seedPouch', name: '丰收种袋', desc: '8 秒内抓住的金色系宝物收益 +40%', cooldown: 24, duration: 8, icon: '✿', stage: 3 },
  lantern: { id: 'lantern', name: '萤光提灯', desc: '10 秒照亮全场，免受断电影响', cooldown: 22, duration: 10, icon: '☼', stage: 2 },
  mossShield: { id: 'mossShield', name: '苔芽护盾', desc: '10 秒内抵挡炸药扣款和生物阻挡', cooldown: 26, duration: 10, icon: '❧', stage: 3 },
  surveyLens: { id: 'surveyLens', name: '探脉望远镜', desc: '8 秒显示钩爪方向上第一件矿物及价值', cooldown: 20, duration: 8, icon: '⌕', stage: 4 },
  starElixir: { id: 'starElixir', name: '星愿药水', desc: '每层一次，下一件可连击宝物收益翻倍', cooldown: 999, duration: 0, icon: '✦', stage: 5 },
  rescueBell: { id:'rescueBell',name:'归还风铃',desc:'驱赶全场生物 6 秒并立即取回被藏矿物',cooldown:26,duration:6,icon:'♧',stage:1 },
  ropeShears: { id:'ropeShears',name:'轻巧剪绳',desc:'丢弃正在搬运的矿物，沿原方向快速收回空钩',cooldown:18,duration:0,icon:'✂',stage:2 },
};

export const COMBO_STEPS = [1, 1.2, 1.4, 1.65] as const;

export interface LevelCurve {
  target: number;
  time: number;
  potentialRatio: number;
  hazards: number;
  creatures: number;
  deepRatio: number;
  swingMultiplier: number;
}
/** Explicit pressure curve: total raw treasure is constrained, never unlimited. */
const LEVEL_CURVES: readonly LevelCurve[] = [
  {target:850,time:38,potentialRatio:1.85,hazards:3,creatures:0,deepRatio:.25,swingMultiplier:.82},
  {target:1100,time:38,potentialRatio:1.75,hazards:4,creatures:1,deepRatio:.30,swingMultiplier:.9},
  {target:1400,time:40,potentialRatio:1.65,hazards:5,creatures:1,deepRatio:.36,swingMultiplier:1},
  {target:1700,time:42,potentialRatio:1.60,hazards:6,creatures:2,deepRatio:.42,swingMultiplier:1.04},
  {target:2050,time:40,potentialRatio:1.58,hazards:7,creatures:2,deepRatio:.48,swingMultiplier:1.08},
  {target:2400,time:42,potentialRatio:1.55,hazards:8,creatures:2,deepRatio:.54,swingMultiplier:1.12},
  {target:2800,time:55,potentialRatio:1.52,hazards:9,creatures:2,deepRatio:.60,swingMultiplier:1.16},
  {target:3150,time:58,potentialRatio:1.50,hazards:10,creatures:3,deepRatio:.65,swingMultiplier:1.20},
  {target:3550,time:48,potentialRatio:1.48,hazards:10,creatures:3,deepRatio:.70,swingMultiplier:1.24},
  {target:4000,time:60,potentialRatio:1.45,hazards:11,creatures:3,deepRatio:.75,swingMultiplier:1.28},
];
export function levelCurve(level: number): LevelCurve {
  const safe=Number.isFinite(level)?Math.max(1,Math.min(10,Math.floor(level))):1;
  return {...LEVEL_CURVES[safe-1]};
}
export const MINERAL_TIME_CAP = 8;
export const DYNAMITE_PENALTY = 75;
export const HOOK_HIT_RADIUS = .18;

export const COLORS = {
  bg: '#483c32',
  ink: '#E8EEF8',
  gold: '#F5C542',
  goldHot: '#FFE08A',
  cyan: '#92bfae',
  magenta: '#d79caa',
  danger: '#FF5A5F',
  rock: '#5B6478',
  panel: 'rgba(10, 18, 34, 0.78)',
  glow: '0 0 24px rgba(245, 197, 66, 0.35)',
};

export const WORLD = {
  halfWidth: 11.5,
  topY: 8.2,
  floorY: -13,
  pivotY: 5.4,
  cameraZ: 22,
  maxHookLength: 23,
  swingAmplitude: 0.92,
  swingSpeed: 0.7,
  extendSpeed: 14,
  baseRetractSpeed: 9,
};

export const PLAYER_COLORS = ['#F5C542', '#92bfae'] as const;

export const LEVEL_NAMES = ['林间初见', '叶影蜜坡', '薄荷晶洞', '晶簇回声', '蘑菇小径', '莓红花园', '古树遗迹', '根间秘藏', '月眠湖畔', '星辉归途'] as const;
